import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as FileSaver from 'file-saver';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';
import { buildSearchPayload, mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { SelectionAggregator } from '../../../../shared/utils/selection-aggregator';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';

import { SummaryTicketModel } from '../../models/summary-ticket.model';
import { LoadTickets, LoadTicketsFailure, LoadTicketsSuccess, TicketActionTypes } from '../../store/actions/ticket.actions';
import { createTicketTableConfig, TableRowAction } from './ticket-table.config';
import { TicketService as TicketServiceV2 } from '../../services/ticket.service';
import { OldTicketService } from '../../services/old-ticket.service';
import { TicketService } from '../../../../../modules/invoice/tickets/ticket.service';
import { EmailDialogComponent } from '../../../../../modules/invoice/tickets/email-dialog.component';
import { TicketDialogTotalComponent } from '../../../../../modules/invoice/tickets/ticket-dialog-total.component';
import { InvoiceDialogVerifactuComponent } from '../../../../../modules/invoice/invoice/invoice-dialog-verifactu.component';
import { InvoiceDialogPaidedComponent } from '../../../../../modules/invoice/invoice/invoice-dialog-paided.component';
import { InvoiceDialogProjectComponent } from '../../../../../modules/invoice/invoice/invoice-dialog-project.component';
import { ActivityService } from '../../../../../modules/user/activity/activity.service';
import { ActualSelected, Utils } from '../../../../../shared/utils';

@Component({
  selector: 'app-ticket-list',
  templateUrl: './ticket-list.components.html',
  styleUrls: ['./ticket-list.components.css'],
  standalone: false
})
export class TicketListV2Component implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('invoiceSummary', { static: false }) invoiceSummary!: TemplateRef<any>;
  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;
  detailLoader = (uid: string) => this.serviceV2.getSummaryByUid(uid);

  monthsControl: FormControl = new FormControl([]);
  quickSearch = '';
  private readonly quickSearch$ = new Subject<string>();

  icon = 'receipt';
  title = 'Facturas simplificadas';
  subTitle = 'Consulta, edita o gestiona tus facturas simplificadas';

  summary: any = null;
  selectedIds: string[] = [];
  selectionTotals = new SelectionAggregator({
    total: (r: any) => r.total,
    paided: (r: any) => r.fullPaid,
    noPaided: (r: any) => (r.total || 0) - (r.fullPaid || 0),
  });

  private isProjectEnable = false;
  private lastState: TableStateSnapshot = {
    pageIndex: 0,
    pageSize: 10,
    filters: {},
    sortField: '',
    sortDirection: '',
  };

  private readonly selectedRows = new Map<string, SummaryTicketModel>();
  private destroy$ = new Subject<void>();
  private subs: Subscription[] = [];

  constructor(
    private store: Store<any>,
    private actions$: Actions,
    private router: Router,
    private dialog: MatDialog,
    private ticketService: TicketService,
    private serviceV2: TicketServiceV2,
    private oldTicketService: OldTicketService,
    private activityService: ActivityService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private loading: LoadingDialogService,
    private tier: TierService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.activityService.loadData(ActualSelected.activitySelected)
      .then(activity => { this.isProjectEnable = !!activity && activity.isProject === true; })
      .catch(() => { this.isProjectEnable = false; });

    let defaultFilter: any = {};
    const stored = localStorage.getItem('viewSummaryTicketFilters');
    if (stored) {
      try {
        defaultFilter = JSON.parse(stored);
        if (defaultFilter.pageSize == null) defaultFilter.pageSize = 10;
      } catch (e) { defaultFilter = {}; }
    }

    this.tableConfig = createTicketTableConfig(
      {
        actionMap: {
          [TableRowAction.Pdf]: (row) => this.downloadPdf(row),
          [TableRowAction.TicketGift]: (row) => this.downloadGiftPdf(row),
          [TableRowAction.PdfMail]: (row) => this.sendPdfByEmail(row),
          [TableRowAction.Charge]: (row) => this.openPayments(row),
          [TableRowAction.Project]: (row) => this.project(row),
          [TableRowAction.Rectify]: (row) => this.rectify(row),
          [TableRowAction.Verifactu]: (row) => this.showVerifactuDetail(row),
        },
        selectable: true,
        summaryTemplate: this.invoiceSummary,
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.newTicket() },
        ],
        gridActions: [
          { label: 'Cobrar facturas simplificadas', icon: 'payment', type: 'button', callback: () => this.payTicketsSelected() },
          { label: 'Totalizar', icon: 'assessment', type: 'button', callback: () => this.openTotalizar() },
        ],
        rowConfig: {
          onToggleSelect: (row: any, selected: boolean) => {
            const id = row.uid;
            if (selected) {
              this.addSelected(id);
              this.selectedRows.set(id, row);
              this.selectionTotals.add(row);
            } else {
              this.removeSelected(id);
              this.selectedRows.delete(id);
              this.selectionTotals.remove(row);
            }
          },
        },
      },
      {
        filterView: 'viewSummaryTicketFilters',
        defaultFilter,
        isProjectEnable: () => this.isProjectEnable,
      }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => {},
      onCollapse: () => {},
      onToggleExpand: () => {},
    };

    this.subs.push(
      this.actions$
        .pipe(
          ofType<LoadTicketsSuccess>(TicketActionTypes.LoadTicketsSuccess),
          map(action => action.payload)
        )
        .subscribe(res => {
          const expanded = new Set((this.t1.tableState.snapshot.data || []).filter((r: any) => r.__expanded).map((r: any) => r.uid));
          const items = ((res.items as any[]) || []).map(item => ({
            ...item,
            __selected: this.selectedIds.indexOf(item.uid) !== -1,
            __expanded: expanded.has(item.uid),
          }));
          this.summary = res.summary;
          this.t1.tableState.setData(items);
          this.t1.tableState.setTotalItems(res.total);
          this.t1.tableState.setLoading(false);
        })
    );

    this.subs.push(
      this.actions$
        .pipe(ofType<LoadTicketsFailure>(TicketActionTypes.LoadTicketsFailure))
        .subscribe(() => {
          this.t1.tableState.setLoading(false);
          this.notify.error('No se pudieron cargar las facturas simplificadas');
        })
    );
  }

  ngAfterViewInit(): void {
    this.tableConfig.summaryTemplate = this.invoiceSummary;
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.monthsControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        const raw = Array.isArray(value) ? value : [];
        const clean = raw.filter((v: any) => typeof v === 'number');
        if (clean.length !== raw.length) {
          this.monthsControl.setValue(clean, { emitEvent: false });
        }
        this.t1.tableState.patchFilters({ months: clean });
      });

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(s => (s.filters && (s.filters as any).search) || ''),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(v => { if (v !== this.quickSearch) this.quickSearch = v as string; });

    this.restoreStoredState();

    this.t1.tableState.state$
      .pipe(
        map(s => ((s.filters && (s.filters as any).months) || []) as number[]),
        distinctUntilChanged((a, b) => a.length === b.length && a.every((v, i) => v === b[i])),
        takeUntil(this.destroy$),
      )
      .subscribe(months => this.monthsControl.setValue(months, { emitEvent: false }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        this.lastState = state;
        localStorage.setItem('viewSummaryTicketFilters', JSON.stringify(state));
        this.dispatchLoad();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subs.forEach(s => s.unsubscribe());
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  clearSelection(): void {
    this.selectedIds = [];
    this.selectedRows.clear();
    this.selectionTotals.reset();
    const data = (this.t1.tableState.snapshot.data || []).map((row: any) => ({ ...row, __selected: false }));
    this.t1.tableState.setData(data);
  }

  reloadCurrentPage(): void {
    this.dispatchLoad();
  }

  newTicket(): void {
    this.router.navigate(['/invoice/ticket/create']);
  }

  downloadPdf(row: SummaryTicketModel): void {
    this.loading.run('Generando PDF…', this.oldTicketService.pdf(row.uid))
      .then(blob => {
        FileSaver.saveAs(blob, `Ticket_${row.ticketNumber}.pdf`);
        this.notify.ok('PDF generado correctamente');
      })
      .catch(() => this.notify.error('No se pudo generar el PDF'));
  }

  downloadGiftPdf(row: SummaryTicketModel): void {
    this.loading.run('Generando ticket regalo…', this.oldTicketService.pdfRegalo(row.uid))
      .then(blob => {
        FileSaver.saveAs(blob, `Ticket_Regalo_${row.ticketNumber}.pdf`);
        this.notify.ok('Ticket regalo generado correctamente');
      })
      .catch(() => this.notify.error('No se pudo generar el ticket regalo'));
  }

  sendPdfByEmail(row: SummaryTicketModel): void {
    EmailDialogComponent.open(this.dialog, {
      email: '',
      title: 'Enviar factura simplificada',
      subtitle: row.ticketNumber,
      send: email => this.oldTicketService.pdfMailTicket(row.uid, encodeURIComponent(email)),
    }).subscribe(email => {
      if (email) this.notify.ok(`Factura simplificada enviada a ${email}`);
    });
  }

  openPayments(row: SummaryTicketModel): void {
    if (!this.tier.requireAdvancedTier()) return;
    InvoiceDialogPaidedComponent.open(this.dialog, { uid: row.uid, isTicket: true, isIncome: true })
      .subscribe(changed => {
        if (!changed) return;
        if (changed !== 'attach-failed') this.notify.ok('Cobros actualizados');
        this.reloadCurrentPage();
      });
  }

  project(row: SummaryTicketModel): void {
    this.dialog.open(InvoiceDialogProjectComponent, {
      width: '560px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: { uid: row.uid, invoice: row, type: 'ticket' },
    }).afterClosed().subscribe(saved => {
      if (saved) this.reloadCurrentPage();
    });
  }

  rectify(row: SummaryTicketModel): void {
    this.confirm.open({
      title: 'Rectificar factura simplificada',
      subtitle: row.ticketNumber || undefined,
      message: 'Se creará una factura simplificada rectificativa con importe negativo. Esta acción no se puede deshacer.',
      tone: 'danger',
      icon: 'history',
      details: this.ticketDetails(row),
      confirmText: 'Rectificar',
      confirmIcon: 'history',
      loadingText: 'Rectificando factura simplificada…',
      errorText: 'No se pudo rectificar la factura simplificada',
      action: () => this.ticketService.loadData(row.uid).then(ticket => {
        ticket.rectifying = true;
        return this.ticketService.editData(ticket.uid, ticket);
      }),
    }).subscribe(rectified => {
      if (!rectified) return;
      this.notify.ok('Factura simplificada rectificada correctamente');
      this.reloadCurrentPage();
    });
  }

  showVerifactuDetail(row: SummaryTicketModel): void {
    this.dialog.open(InvoiceDialogVerifactuComponent, {
      width: '560px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: { uid: row.uid, type: 'ticket', number: row.ticketNumber },
    });
  }

  payTicketsSelected(): void {
    if (!this.tier.requireAdvancedTier()) return;
    if (this.selectedIds.length === 0) {
      this.notify.warning('Selecciona las facturas simplificadas que quieres cobrar');
      return;
    }

    const rows = this.selectedIds.map(uid => this.selectedRows.get(uid)).filter(r => !!r) as SummaryTicketModel[];
    const valid = rows.filter(r => !r.collectionDate && !r.rectifying);
    if (valid.length === 0) {
      this.notify.warning('Las facturas simplificadas seleccionadas ya están cobradas o rectificadas');
      return;
    }
    if (valid.length < rows.length) {
      const skipped = rows.length - valid.length;
      this.notify.warning(`Se omiten ${skipped} ${skipped === 1 ? 'factura simplificada ya cobrada o rectificada' : 'facturas simplificadas ya cobradas o rectificadas'}`);
    }

    const amountSelectedUnpaid = valid.reduce((sum, r) => sum + Utils.GetValueWithDecimals((r.total || 0) - (r.fullPaid || 0)), 0);

    InvoiceDialogPaidedComponent.open(this.dialog, {
      selectedUids: valid.map(r => r.uid), isTicket: true, isIncome: true, isMultiple: true, amountSelectedUnpaid,
    }).subscribe(changed => {
      if (!changed) return;
      this.notify.ok('Facturas simplificadas cobradas correctamente');
      this.clearSelection();
      this.reloadCurrentPage();
    });
  }

  openTotalizar(): void {
    if (!this.tier.requireAdvancedTier()) return;
    TicketDialogTotalComponent.open(this.dialog, { activityUid: ActualSelected.activitySelected });
  }

  private ticketDetails(row: SummaryTicketModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.receiverName) details.push({ label: 'Cliente', value: row.receiverName });
    if (row.date) details.push({ label: 'Fecha', value: formatDate(row.date, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: 'Total', value: Utils.FormatMoney(row.total) + ' €' });
    return details;
  }

  private restoreStoredState(): void {
    const stored = this.tableConfig.defaultFilter as Partial<TableStateSnapshot>;
    if (!stored) return;

    const tableState = this.t1.tableState;
    if (stored.pageSize) tableState.setPageSize(stored.pageSize);
    if (stored.filters && Object.keys(stored.filters).length) tableState.setFilters(stored.filters);
    if (stored.sortField) tableState.setSort(stored.sortField, stored.sortDirection || '');
  }

  private dispatchLoad(): void {
    const filter = { ...this.lastState, filters: { ...this.lastState.filters, year: ActualSelected.yearSelected } };
    this.t1.tableState.setLoading(true);
    this.store.dispatch(new LoadTickets({ filter: buildSearchPayload(filter) }));
  }

  private addSelected(id: string): void {
    if (this.selectedIds.indexOf(id) === -1) this.selectedIds.push(id);
  }

  private removeSelected(id: string): void {
    const index = this.selectedIds.indexOf(id);
    if (index !== -1) this.selectedIds.splice(index, 1);
  }
}
